import { useEffect, useState, useContext, useCallback } from 'react';
import courseService from 'ApiService/Admin/courseService';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const SelectBatches = ({
  selectedDepartment,
  teacherData,
  selectedCourse,
  setSelectedCourse,
  selectedBatch,
  setSelectedBatch,
  selectionIds,
  setSelectionIds,
  selectionTitle,
  setSelectionTitle,
  setDisable,
}) => {
  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState('');
  const [loading, setLoading] = useState(false);
  const { state } = useContext(AuthContext);

  const fetchCourseData = useCallback(async () => {
    if (!selectedDepartment) return;
    setLoading(true);
    try {
      const response = await courseService.getCourseBatchInfo({
        departmentId: selectedDepartment,
        selectedBranch: state?.selectedBranch,
      });
      if (response.data.success) {
        setCourses(response.data.courses || []);
      } else {
        toast.error('Failed to fetch courses.');
        setCourses([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment, state?.selectedBranch]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  useEffect(() => {
    setDisable(selectionIds?.length === 0);
  }, [selectionIds, setDisable]);

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setSelectedCourse(courseId);
    setSelectedStructure('');
    setSelectedBatch(null);
    setBatches([]);
    
    const course = courses?.find((c) => c.courseId === courseId);
    setBatchStructures(course?.batchStructures || []);
  };

  const handleStructureChange = (e) => {
    const structureId = e.target.value;
    setSelectedStructure(structureId);
    setSelectedBatch(null);
    
    const structure = batchStructures?.find((s) => s.batchStructureId === structureId);
    setBatches(structure?.batches || []);
  };

  const handleBatchChange = (e) => {
    setSelectedBatch(e.target.value);
  };

  const handleSelection = () => {
    if (!selectedCourse || !selectedStructure || !selectedBatch) return;

    const courseObj = courses?.find((c) => c.courseId === selectedCourse);
    const structureObj = batchStructures?.find((s) => s.batchStructureId === selectedStructure);
    const batchObj = batches?.find((b) => b._id === selectedBatch);

    // Prevent duplicates
    if (selectionIds?.find(s => s.batch === selectedBatch)) {
      return toast.error('This batch is already added.');
    }

    setSelectionIds([
      ...(selectionIds || []),
      { 
        course: selectedCourse, 
        batchStructure: selectedStructure, 
        batch: selectedBatch 
      },
    ]);

    setSelectionTitle([
      ...(selectionTitle || []),
      {
        courseName: courseObj?.courseName || 'Unknown Course',
        structureName: structureObj?.batchStructureName || 'Unknown Structure',
        batchName: `Batch ${batchObj?.batchNumber}`,
        branches: batchObj?.branches?.join(', ') || 'N/A'
      },
    ]);

    // Reset selection for next add
    setSelectedBatch(null);
  };

  const removeSelectedBatch = (indexToRemove) => {
    setSelectionIds((selectionIds || []).filter((_, i) => i !== indexToRemove));
    setSelectionTitle((selectionTitle || []).filter((_, i) => i !== indexToRemove));
  };

  if (!teacherData?.teacherId) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-500">
        Please select a teacher first.
      </div>
    );
  }

  return (
    <div className="mt-4 h-full w-full rounded-[20px] p-5 dark:!bg-navy-800 md:mt-5">
      <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
        Assign Batches
      </h2>

      <div className="space-y-4 rounded-[10px] border p-4 dark:border-white/10">
        {/* Course Selection */}
        <div>
          <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            value={selectedCourse || ''}
            onChange={handleCourseChange}
          >
            <option value="">Click here to Select Course</option>
            {courses?.map((course) => (
              <option key={course.courseId} value={course.courseId}>
                {course.courseName} {course.isGlobal ? '(Global)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Batch Structure Selection */}
        {selectedCourse && (
          <div>
            <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
              Select Batch Structure
            </label>
            <select
              className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              value={selectedStructure}
              onChange={handleStructureChange}
            >
              <option value="">Click here to Select Structure</option>
              {batchStructures?.map((structure) => (
                <option key={structure.batchStructureId} value={structure.batchStructureId}>
                  {structure.batchStructureName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Batch Selection */}
        {selectedStructure && (
          <div>
            <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
              Select Batch
            </label>
            <select
              className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              value={selectedBatch || ''}
              onChange={handleBatchChange}
            >
              <option value="">Click here to Select Batch</option>
              {batches?.map((batch) => (
                <option key={batch._id} value={batch._id}>
                  Batch {batch.batchNumber} ({batch.branches?.join(', ')})
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={handleSelection}
          disabled={!selectedBatch}
          className={`mt-2 w-full rounded-xl py-3 text-sm font-bold transition-all ${
            selectedBatch
              ? 'bg-brand-500 text-white hover:bg-brand-600'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          + Add to Selection
        </button>
      </div>

      {/* Selected List */}
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
          Selected Batches ({selectionTitle?.length || 0})
        </h3>
        <div className="space-y-3">
          {(!selectionTitle || selectionTitle.length === 0) ? (
            <p className="py-4 text-center text-sm text-gray-500">No batches added yet.</p>
          ) : (
            selectionTitle.map((data, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-white/10 dark:bg-navy-700"
              >
                <div className="space-y-1">
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {data.courseName}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Structure: <span className="font-medium">{data.structureName}</span>
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {data.batchName} <span className="ml-1 opacity-60">({data.branches})</span>
                  </p>
                </div>
                <button
                  onClick={() => removeSelectedBatch(i)}
                  className="rounded-lg p-2 text-red-500 hover:bg-red-50 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm dark:bg-navy-900/60">
          <BallTriangle color="#422AFB" />
        </div>
      )}
    </div>
  );
};

export default SelectBatches;
